import React, { memo, useMemo, ReactNode } from 'react';
import { Breakpoint } from '../../../interfaces/types/Breakpoint.type';

export type ColIndexes =
  | 'auto'
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12;

interface ColConfig {
  default?: ColIndexes;
  xs?: ColIndexes;
  sm?: ColIndexes;
  md?: ColIndexes;
  lg?: ColIndexes;
  xl?: ColIndexes;
  xxl?: ColIndexes;
}

export interface ColProps {
  children: ReactNode;
  span?: ColConfig;
  start?: ColConfig;
  end?: ColConfig;
  order?: ColConfig;
  hideUntilBreakpoint?: Breakpoint;
}

const Col: React.FC<ColProps> = ({
  children,
  span = undefined,
  start = undefined,
  end = undefined,
  order = undefined,
  hideUntilBreakpoint = undefined,
}) => {
  /*-- col span logic --*/
  const defaultColSpanClassNames: string = useMemo(() => {
    if (span?.default) {
      switch (span.default) {
        case 1:
          return 'col-span-1';
        case 2:
          return 'col-span-2';
        case 3:
          return 'col-span-3';
        case 4:
          return 'col-span-4';
        case 5:
          return 'col-span-5';
        case 6:
          return 'col-span-6';
        case 7:
          return 'col-span-7';
        case 8:
          return 'col-span-8';
        case 9:
          return 'col-span-9';
        case 10:
          return 'col-span-10';
        case 11:
          return 'col-span-11';
        case 12:
          return 'col-span-12';
        default:
          return 'col-span-12';
      }
    } else {
      return 'col-span-12';
    }
  }, [span?.default]);

  const xsColSpanClassNames: string = useMemo(() => {
    if (span?.xs) {
      switch (span.xs) {
        case 1:
          return ' xs:col-span-1';
        case 2:
          return ' xs:col-span-2';
        case 3:
          return ' xs:col-span-3';
        case 4:
          return ' xs:col-span-4';
        case 5:
          return ' xs:col-span-5';
        case 6:
          return ' xs:col-span-6';
        case 7:
          return ' xs:col-span-7';
        case 8:
          return ' xs:col-span-8';
        case 9:
          return ' xs:col-span-9';
        case 10:
          return ' xs:col-span-10';
        case 11:
          return ' xs:col-span-11';
        case 12:
          return ' xs:col-span-12';
        default:
          return '';
      }
    } else {
      return '';
    }
  }, [span?.xs]);

  const smColSpanClassNames = useMemo(() => {
    if (span?.sm) {
      switch (span.sm) {
        case 1:
          return 'sm:col-span-1';
        case 2:
          return 'sm:col-span-2';
        case 3:
          return 'sm:col-span-3';
        case 4:
          return 'sm:col-span-4';
        case 5:
          return 'sm:col-span-5';
        case 6:
          return 'sm:col-span-6';
        case 7:
          return 'sm:col-span-7';
        case 8:
          return 'sm:col-span-8';
        case 9:
          return 'sm:col-span-9';
        case 10:
          return 'sm:col-span-10';
        case 11:
          return 'sm:col-span-11';
        case 12:
          return 'sm:col-span-12';
        default:
          return '';
      }
    } else {
      return '';
    }
  }, [span?.sm]);

  const mdColSpanClassNames = useMemo(() => {
    if (span?.md) {
      switch (span.md) {
        case 1:
          return 'md:col-span-1';
        case 2:
          return 'md:col-span-2';
        case 3:
          return 'md:col-span-3';
        case 4:
          return 'md:col-span-4';
        case 5:
          return 'md:col-span-5';
        case 6:
          return 'md:col-span-6';
        case 7:
          return 'md:col-span-7';
        case 8:
          return 'md:col-span-8';
        case 9:
          return 'md:col-span-9';
        case 10:
          return 'md:col-span-10';
        case 11:
          return 'md:col-span-11';
        case 12:
          return 'md:col-span-12';
        default:
          return '';
      }
    } else {
      return '';
    }
  }, [span?.md]);

  const lgColSpanClassNames = useMemo(() => {
    if (span?.lg) {
      switch (span.lg) {
        case 1:
          return 'lg:col-span-1';
        case 2:
          return 'lg:col-span-2';
        case 3:
          return 'lg:col-span-3';
        case 4:
          return 'lg:col-span-4';
        case 5:
          return 'lg:col-span-5';
        case 6:
          return 'lg:col-span-6';
        case 7:
          return 'lg:col-span-7';
        case 8:
          return 'lg:col-span-8';
        case 9:
          return 'lg:col-span-9';
        case 10:
          return 'lg:col-span-10';
        case 11:
          return 'lg:col-span-11';
        case 12:
          return 'lg:col-span-12';
        default:
          return '';
      }
    } else {
      return '';
    }
  }, [span?.lg]);

  const xlColSpanClassNames = useMemo(() => {
    if (span?.xl) {
      switch (span.xl) {
        case 1:
          return 'xl:col-span-1';
        case 2:
          return 'xl:col-span-2';
        case 3:
          return 'xl:col-span-3';
        case 4:
          return 'xl:col-span-4';
        case 5:
          return 'xl:col-span-5';
        case 6:
          return 'xl:col-span-6';
        case 7:
          return 'xl:col-span-7';
        case 8:
          return 'xl:col-span-8';
        case 9:
          return 'xl:col-span-9';
        case 10:
          return 'xl:col-span-10';
        case 11:
          return 'xl:col-span-11';
        case 12:
          return 'xl:col-span-12';
        default:
          return '';
      }
    } else {
      return '';
    }
  }, [span?.xl]);

  const xxlColSpanClassNames = useMemo(() => {
    if (span?.xxl) {
      switch (span.xxl) {
        case 1:
          return '2xl:col-span-1';
        case 2:
          return '2xl:col-span-2';
        case 3:
          return '2xl:col-span-3';
        case 4:
          return '2xl:col-span-4';
        case 5:
          return '2xl:col-span-5';
        case 6:
          return '2xl:col-span-6';
        case 7:
          return '2xl:col-span-7';
        case 8:
          return '2xl:col-span-8';
        case 9:
          return '2xl:col-span-9';
        case 10:
          return '2xl:col-span-10';
        case 11:
          return '2xl:col-span-11';
        case 12:
          return '2xl:col-span-12';
        default:
          return '';
      }
    } else {
      return '';
    }
  }, [span?.xxl]);
  /*-- end col span logic --*/

  /*-- col start logic --*/
  const defaultColStartClassNames = useMemo(() => {
    if (start?.default) {
      switch (start.default) {
        case 'auto':
          return 'col-start-auto';
        case 1:
          return 'col-start-1';
        case 2:
          return 'col-start-2';
        case 3:
          return 'col-start-3';
        case 4:
          return 'col-start-4';
        case 5:
          return 'col-start-5';
        case 6:
          return 'col-start-6';
        case 7:
          return 'col-start-7';
        case 8:
          return 'col-start-8';
        case 9:
          return 'col-start-9';
        case 10:
          return 'col-start-10';
        case 11:
          return 'col-start-11';
        case 12:
          return 'col-start-12';
      }
    } else {
      return '';
    }
  }, [start?.default]);

  const xsColStartClassNames = useMemo(() => {
    if (start?.xs) {
      switch (start.xs) {
        case 'auto':
          return 'xs:col-start-auto';
        case 1:
          return 'xs:col-start-1';
        case 2:
          return 'xs:col-start-2';
        case 3:
          return 'xs:col-start-3';
        case 4:
          return 'xs:col-start-4';
        case 5:
          return 'xs:col-start-5';
        case 6:
          return 'xs:col-start-6';
        case 7:
          return 'xs:col-start-7';
        case 8:
          return 'xs:col-start-8';
        case 9:
          return 'xs:col-start-9';
        case 10:
          return 'xs:col-start-10';
        case 11:
          return 'xs:col-start-11';
        case 12:
          return 'xs:col-start-12';
      }
    } else {
      return '';
    }
  }, [start?.xs]);

  const smColStartClassNames = useMemo(() => {
    if (start?.sm) {
      switch (start.sm) {
        case 'auto':
          return 'sm:col-start-auto';
        case 1:
          return 'sm:col-start-1';
        case 2:
          return 'sm:col-start-2';
        case 3:
          return 'sm:col-start-3';
        case 4:
          return 'sm:col-start-4';
        case 5:
          return 'sm:col-start-5';
        case 6:
          return 'sm:col-start-6';
        case 7:
          return 'sm:col-start-7';
        case 8:
          return 'sm:col-start-8';
        case 9:
          return 'sm:col-start-9';
        case 10:
          return 'sm:col-start-10';
        case 11:
          return 'sm:col-start-11';
        case 12:
          return 'sm:col-start-12';
      }
    } else {
      return '';
    }
  }, [start?.sm]);

  const mdColStartClassNames = useMemo(() => {
    if (start?.md) {
      switch (start.md) {
        case 'auto':
          return 'md:col-start-auto';
        case 1:
          return 'md:col-start-1';
        case 2:
          return 'md:col-start-2';
        case 3:
          return 'md:col-start-3';
        case 4:
          return 'md:col-start-4';
        case 5:
          return 'md:col-start-5';
        case 6:
          return 'md:col-start-6';
        case 7:
          return 'md:col-start-7';
        case 8:
          return 'md:col-start-8';
        case 9:
          return 'md:col-start-9';
        case 10:
          return 'md:col-start-10';
        case 11:
          return 'md:col-start-11';
        case 12:
          return 'md:col-start-12';
      }
    } else {
      return '';
    }
  }, [start?.md]);

  const lgColStartClassNames = useMemo(() => {
    if (start?.lg) {
      switch (start.lg) {
        case 'auto':
          return 'lg:col-start-auto';
        case 1:
          return 'lg:col-start-1';
        case 2:
          return 'lg:col-start-2';
        case 3:
          return 'lg:col-start-3';
        case 4:
          return 'lg:col-start-4';
        case 5:
          return 'lg:col-start-5';
        case 6:
          return 'lg:col-start-6';
        case 7:
          return 'lg:col-start-7';
        case 8:
          return 'lg:col-start-8';
        case 9:
          return 'lg:col-start-9';
        case 10:
          return 'lg:col-start-10';
        case 11:
          return 'lg:col-start-11';
        case 12:
          return 'lg:col-start-12';
      }
    } else {
      return '';
    }
  }, [start?.lg]);

  const xlColStartClassNames = useMemo(() => {
    if (start?.xl) {
      switch (start.xl) {
        case 'auto':
          return 'xl:col-start-auto';
        case 1:
          return 'xl:col-start-1';
        case 2:
          return 'xl:col-start-2';
        case 3:
          return 'xl:col-start-3';
        case 4:
          return 'xl:col-start-4';
        case 5:
          return 'xl:col-start-5';
        case 6:
          return 'xl:col-start-6';
        case 7:
          return 'xl:col-start-7';
        case 8:
          return 'xl:col-start-8';
        case 9:
          return 'xl:col-start-9';
        case 10:
          return 'xl:col-start-10';
        case 11:
          return 'xl:col-start-11';
        case 12:
          return 'xl:col-start-12';
      }
    } else {
      return '';
    }
  }, [start?.xl]);

  const xxlColStartClassNames = useMemo(() => {
    if (start?.xxl) {
      switch (start.xxl) {
        case 'auto':
          return '2xl:col-start-auto';
        case 1:
          return '2xl:col-start-1';
        case 2:
          return '2xl:col-start-2';
        case 3:
          return '2xl:col-start-3';
        case 4:
          return '2xl:col-start-4';
        case 5:
          return '2xl:col-start-5';
        case 6:
          return '2xl:col-start-6';
        case 7:
          return '2xl:col-start-7';
        case 8:
          return '2xl:col-start-8';
        case 9:
          return '2xl:col-start-9';
        case 10:
          return '2xl:col-start-10';
        case 11:
          return '2xl:col-start-11';
        case 12:
          return '2xl:col-start-12';
      }
    } else {
      return '';
    }
  }, [start?.xxl]);
  /*-- end col start logic --*/

  /*-- col end logic --*/
  const defaultColEndClassNames = useMemo(() => {
    if (end?.default) {
      switch (end.default) {
        case 'auto':
          return 'col-end-auto';
        case 1:
          return 'col-end-1';
        case 2:
          return 'col-end-2';
        case 3:
          return 'col-end-3';
        case 4:
          return 'col-end-4';
        case 5:
          return 'col-end-5';
        case 6:
          return 'col-end-6';
        case 7:
          return 'col-end-7';
        case 8:
          return 'col-end-8';
        case 9:
          return 'col-end-9';
        case 10:
          return 'col-end-10';
        case 11:
          return 'col-end-11';
        case 12:
          return 'col-end-12';
      }
    } else {
      return '';
    }
  }, [end?.default]);

  const xsColEndClassNames = useMemo(() => {
    if (end?.xs) {
      switch (end.xs) {
        case 'auto':
          return 'xs:col-end-auto';
        case 1:
          return 'xs:col-end-1';
        case 2:
          return 'xs:col-end-2';
        case 3:
          return 'xs:col-end-3';
        case 4:
          return 'xs:col-end-4';
        case 5:
          return 'xs:col-end-5';
        case 6:
          return 'xs:col-end-6';
        case 7:
          return 'xs:col-end-7';
        case 8:
          return 'xs:col-end-8';
        case 9:
          return 'xs:col-end-9';
        case 10:
          return 'xs:col-end-10';
        case 11:
          return 'xs:col-end-11';
        case 12:
          return 'xs:col-end-12';
      }
    } else {
      return '';
    }
  }, [end?.xs]);

  const smColEndClassNames = useMemo(() => {
    if (end?.sm) {
      switch (end.sm) {
        case 'auto':
          return 'sm:col-end-auto';
        case 1:
          return 'sm:col-end-1';
        case 2:
          return 'sm:col-end-2';
        case 3:
          return 'sm:col-end-3';
        case 4:
          return 'sm:col-end-4';
        case 5:
          return 'sm:col-end-5';
        case 6:
          return 'sm:col-end-6';
        case 7:
          return 'sm:col-end-7';
        case 8:
          return 'sm:col-end-8';
        case 9:
          return 'sm:col-end-9';
        case 10:
          return 'sm:col-end-10';
        case 11:
          return 'sm:col-end-11';
        case 12:
          return 'sm:col-end-12';
      }
    } else {
      return '';
    }
  }, [end?.sm]);

  const mdColEndClassNames = useMemo(() => {
    if (end?.md) {
      switch (end.md) {
        case 'auto':
          return 'md:col-end-auto';
        case 1:
          return 'md:col-end-1';
        case 2:
          return 'md:col-end-2';
        case 3:
          return 'md:col-end-3';
        case 4:
          return 'md:col-end-4';
        case 5:
          return 'md:col-end-5';
        case 6:
          return 'md:col-end-6';
        case 7:
          return 'md:col-end-7';
        case 8:
          return 'md:col-end-8';
        case 9:
          return 'md:col-end-9';
        case 10:
          return 'md:col-end-10';
        case 11:
          return 'md:col-end-11';
        case 12:
          return 'md:col-end-12';
      }
    } else {
      return '';
    }
  }, [end?.md]);

  const lgColEndClassNames = useMemo(() => {
    if (end?.lg) {
      switch (end.lg) {
        case 'auto':
          return 'lg:col-end-auto';
        case 1:
          return 'lg:col-end-1';
        case 2:
          return 'lg:col-end-2';
        case 3:
          return 'lg:col-end-3';
        case 4:
          return 'lg:col-end-4';
        case 5:
          return 'lg:col-end-5';
        case 6:
          return 'lg:col-end-6';
        case 7:
          return 'lg:col-end-7';
        case 8:
          return 'lg:col-end-8';
        case 9:
          return 'lg:col-end-9';
        case 10:
          return 'lg:col-end-10';
        case 11:
          return 'lg:col-end-11';
        case 12:
          return 'lg:col-end-12';
      }
    } else {
      return '';
    }
  }, [end?.lg]);

  const xlColEndClassNames = useMemo(() => {
    if (end?.xl) {
      switch (end.xl) {
        case 'auto':
          return 'xl:col-end-auto';
        case 1:
          return 'xl:col-end-1';
        case 2:
          return 'xl:col-end-2';
        case 3:
          return 'xl:col-end-3';
        case 4:
          return 'xl:col-end-4';
        case 5:
          return 'xl:col-end-5';
        case 6:
          return 'xl:col-end-6';
        case 7:
          return 'xl:col-end-7';
        case 8:
          return 'xl:col-end-8';
        case 9:
          return 'xl:col-end-9';
        case 10:
          return 'xl:col-end-10';
        case 11:
          return 'xl:col-end-11';
        case 12:
          return 'xl:col-end-12';
      }
    } else {
      return '';
    }
  }, [end?.xl]);

  const xxlColEndClassNames = useMemo(() => {
    if (end?.xxl) {
      switch (end.xxl) {
        case 'auto':
          return '2xl:col-end-auto';
        case 1:
          return '2xl:col-end-1';
        case 2:
          return '2xl:col-end-2';
        case 3:
          return '2xl:col-end-3';
        case 4:
          return '2xl:col-end-4';
        case 5:
          return '2xl:col-end-5';
        case 6:
          return '2xl:col-end-6';
        case 7:
          return '2xl:col-end-7';
        case 8:
          return '2xl:col-end-8';
        case 9:
          return '2xl:col-end-9';
        case 10:
          return '2xl:col-end-10';
        case 11:
          return '2xl:col-end-11';
        case 12:
          return '2xl:col-end-12';
      }
    } else {
      return '';
    }
  }, [end?.xxl]);
  /*-- end col end logic --*/

  /*-- start col order logic --*/
  const defaultColOrderClassNames = useMemo(() => {
    if (order?.default) {
      switch (order.default) {
        case 'auto':
          return ' order-none';
        case 1:
          return ' order-1';
        case 2:
          return ' order-2';
        case 3:
          return ' order-3';
        case 4:
          return ' order-4';
        case 5:
          return ' order-5';
        case 6:
          return ' order-6';
        case 7:
          return ' order-7';
        case 8:
          return ' order-8';
        case 9:
          return ' order-9';
        case 10:
          return ' order-10';
        case 11:
          return ' order-11';
        case 12:
          return ' order-12';
      }
    } else {
      return '';
    }
  }, [order?.default]);

  const xsColOrderClassNames = useMemo(() => {
    if (order?.xs) {
      switch (order.xs) {
        case 'auto':
          return ' xs:order-none';
        case 1:
          return ' xs:order-1';
        case 2:
          return ' xs:order-2';
        case 3:
          return ' xs:order-3';
        case 4:
          return ' xs:order-4';
        case 5:
          return ' xs:order-5';
        case 6:
          return ' xs:order-6';
        case 7:
          return ' xs:order-7';
        case 8:
          return ' xs:order-8';
        case 9:
          return ' xs:order-9';
        case 10:
          return ' xs:order-10';
        case 11:
          return ' xs:order-11';
        case 12:
          return ' xs:order-12';
      }
    } else {
      return '';
    }
  }, [order?.xs]);

  const smColOrderClassNames = useMemo(() => {
    if (order?.sm) {
      switch (order.sm) {
        case 'auto':
          return ' sm:order-none';
        case 1:
          return ' sm:order-1';
        case 2:
          return ' sm:order-2';
        case 3:
          return ' sm:order-3';
        case 4:
          return ' sm:order-4';
        case 5:
          return ' sm:order-5';
        case 6:
          return ' sm:order-6';
        case 7:
          return ' sm:order-7';
        case 8:
          return ' sm:order-8';
        case 9:
          return ' sm:order-9';
        case 10:
          return ' sm:order-10';
        case 11:
          return ' sm:order-11';
        case 12:
          return ' sm:order-12';
      }
    } else {
      return '';
    }
  }, [order?.sm]);

  const mdColOrderClassNames = useMemo(() => {
    if (order?.md) {
      switch (order.md) {
        case 'auto':
          return ' md:order-none';
        case 1:
          return ' md:order-1';
        case 2:
          return ' md:order-2';
        case 3:
          return ' md:order-3';
        case 4:
          return ' md:order-4';
        case 5:
          return ' md:order-5';
        case 6:
          return ' md:order-6';
        case 7:
          return ' md:order-7';
        case 8:
          return ' md:order-8';
        case 9:
          return ' md:order-9';
        case 10:
          return ' md:order-10';
        case 11:
          return ' md:order-11';
        case 12:
          return ' md:order-12';
      }
    } else {
      return '';
    }
  }, [order?.md]);

  const lgColOrderClassNames = useMemo(() => {
    if (end?.lg) {
      switch (end.lg) {
        case 'auto':
          return ' lg:order-none';
        case 1:
          return ' lg:order-1';
        case 2:
          return ' lg:order-2';
        case 3:
          return ' lg:order-3';
        case 4:
          return ' lg:order-4';
        case 5:
          return ' lg:order-5';
        case 6:
          return ' lg:order-6';
        case 7:
          return ' lg:order-7';
        case 8:
          return ' lg:order-8';
        case 9:
          return ' lg:order-9';
        case 10:
          return ' lg:order-10';
        case 11:
          return ' lg:order-11';
        case 12:
          return ' lg:order-12';
      }
    } else {
      return '';
    }
  }, [order?.lg]);

  const xlColOrderClassNames = useMemo(() => {
    if (order?.xl) {
      switch (order.xl) {
        case 'auto':
          return ' xl:order-none';
        case 1:
          return ' xl:order-1';
        case 2:
          return ' xl:order-2';
        case 3:
          return ' xl:order-3';
        case 4:
          return ' xl:order-4';
        case 5:
          return ' xl:order-5';
        case 6:
          return ' xl:order-6';
        case 7:
          return ' xl:order-7';
        case 8:
          return ' xl:order-8';
        case 9:
          return ' xl:order-9';
        case 10:
          return ' xl:order-10';
        case 11:
          return ' xl:order-11';
        case 12:
          return ' xl:order-12';
      }
    } else {
      return '';
    }
  }, [order?.xl]);

  const xxlColOrderClassNames = useMemo(() => {
    if (order?.xxl) {
      switch (order.xxl) {
        case 'auto':
          return ' 2xl:order-none';
        case 1:
          return ' 2xl:order-1';
        case 2:
          return ' 2xl:order-2';
        case 3:
          return ' 2xl:order-3';
        case 4:
          return ' 2xl:order-4';
        case 5:
          return ' 2xl:order-5';
        case 6:
          return ' 2xl:order-6';
        case 7:
          return ' 2xl:order-7';
        case 8:
          return ' 2xl:order-8';
        case 9:
          return ' 2xl:order-9';
        case 10:
          return ' 2xl:order-10';
        case 11:
          return ' 2xl:order-11';
        case 12:
          return ' 2xl:order-12';
      }
    } else {
      return '';
    }
  }, [order?.xxl]);
  /*-- end col order logic --*/

  /*-- start col visible logic --*/
  const xsBecomeVisibleClassNames = useMemo(
    () => (hideUntilBreakpoint === 'xs' ? ' xs:block' : ''),
    [hideUntilBreakpoint]
  );

  const smBecomeVisibleClassNames = useMemo(
    () => (hideUntilBreakpoint === 'sm' ? ' sm:block' : ''),
    [hideUntilBreakpoint]
  );

  const mdBecomeVisibleClassNames = useMemo(
    () => (hideUntilBreakpoint === 'md' ? ' md:block' : ''),
    [hideUntilBreakpoint]
  );

  const lgBecomeVisibleClassNames = useMemo(
    () => (hideUntilBreakpoint === 'lg' ? ' lg:block' : ''),
    [hideUntilBreakpoint]
  );

  const xlBecomeVisibleClassNames = useMemo(
    () => (hideUntilBreakpoint === 'xl' ? ' xl:block' : ''),
    [hideUntilBreakpoint]
  );

  const xxlBecomeVisibleClassNames = useMemo(
    () => (hideUntilBreakpoint === '2xl' ? ' 2xl:block' : ''),
    [hideUntilBreakpoint]
  );

  const memoisedClassNames = useMemo(() => {
    return [
      defaultColSpanClassNames,
      defaultColStartClassNames,
      defaultColEndClassNames,
      xsColSpanClassNames,
      xsColStartClassNames,
      xsColEndClassNames,
      xsBecomeVisibleClassNames,
      smColSpanClassNames,
      smColStartClassNames,
      smColEndClassNames,
      smBecomeVisibleClassNames,
      mdColSpanClassNames,
      mdColStartClassNames,
      mdColEndClassNames,
      mdBecomeVisibleClassNames,
      lgColSpanClassNames,
      lgColStartClassNames,
      lgColEndClassNames,
      lgBecomeVisibleClassNames,
      xlColSpanClassNames,
      xlColStartClassNames,
      xlColEndClassNames,
      xlBecomeVisibleClassNames,
      xxlColSpanClassNames,
      xxlColStartClassNames,
      xxlColEndClassNames,
      xxlBecomeVisibleClassNames,
      defaultColOrderClassNames,
      xsColOrderClassNames,
      smColOrderClassNames,
      mdColOrderClassNames,
      lgColOrderClassNames,
      xlColOrderClassNames,
      xxlColOrderClassNames,
    ].join(' ');
  }, [span, start, end, hideUntilBreakpoint]);

  return (
    <div
      className={`h-full ${
        hideUntilBreakpoint && 'hidden'
      } ${memoisedClassNames}`}
    >
      {children}
    </div>
  );
};

export default memo(Col);
